export type WealthCalculatorInputs = {
  sponsors: number;
  empPerPlan: number;
  turnoverRate: number;
  avgBalance: number;
  convRate: number;
  feeRate: number;
};

export type WealthCalculatorResults = {
  totalParticipants: number;
  assetsInMotion: number;
  flaggedOpportunities: number;
  aumConverted: number;
  annualRevenue: number;
  projection: number[];
};

export const DEFAULT_INPUTS: WealthCalculatorInputs = {
  sponsors: 50,
  empPerPlan: 500,
  turnoverRate: 15,
  avgBalance: 88000,
  convRate: 20,
  feeRate: 1,
};

/** Abbreviates large dollar amounts to K/M/B suffixes for compact display (e.g. $2.1B). */
export const formatCompactDollar = (amount: number): string => {
  if (amount >= 1_000_000_000) return `$${(amount / 1_000_000_000).toFixed(1).replace(/\.0$/, "")}B`;
  if (amount >= 1_000_000) return `$${(amount / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  if (amount >= 1_000) return `$${Math.round(amount / 1_000)}K`;
  return `$${Math.round(amount)}`;
};

/** Formats a number as a locale-aware integer string (e.g. 1,234). */
export const formatCount = (value: number): string => Math.round(value).toLocaleString("en-US");

/** Estimates AUM opportunity from a book of employer plans by modeling job-change rollover assets, held-away assets, and conversion to new revenue at the given fee rate. */
export const calcResults = (inputs: WealthCalculatorInputs): WealthCalculatorResults => {
  const { sponsors, empPerPlan, turnoverRate, avgBalance, convRate, feeRate } = inputs;
  const turnoverRatio = turnoverRate / 100;
  const conversionRatio = convRate / 100;
  const feeRatio = feeRate / 100;
  const totalParticipants = sponsors * empPerPlan;
  const turnoverAssets = totalParticipants * turnoverRatio * avgBalance;
  const stableParticipants = totalParticipants * (1 - turnoverRatio);
  const heldAwayAssets = stableParticipants * 0.12 * avgBalance * 0.6;
  const assetsInMotion = turnoverAssets + heldAwayAssets;
  const flaggedOpportunities = assetsInMotion * 0.5;
  const aumConverted = flaggedOpportunities * conversionRatio;
  const annualRevenue = aumConverted * feeRatio;
  const projection = Array.from({ length: 5 }, (_, yearIndex) =>
    Array.from({ length: yearIndex + 1 }, (__, cohortIndex) =>
      aumConverted * Math.pow(1.08, yearIndex - cohortIndex)
    ).reduce((sum, value) => sum + value, 0)
  );
  return { totalParticipants, assetsInMotion, flaggedOpportunities, aumConverted, annualRevenue, projection };
};
